import { collect } from 'collect.js';
import { ScreenEnum, ScreenGroupEnum } from '../../hooks/navigation/Screens';
import str from '../helpers/Stringable';
import { ENVIRONMENTS } from '../exports/Constants';

/**
 * Pure RBAC / multi-tenant ACL resolution.
 *
 * This module holds the role→permission and role→screen decision matrices plus
 * the pure functions that evaluate them. `hooks/auth/usePermissions.js` is a thin
 * wrapper that reads the current Redux state, builds a plain `ctx` snapshot, and
 * delegates here. Keeping the logic pure (no React/Redux) makes it exhaustively
 * unit-testable — see tests/services/permissions/PermissionResolver.test.js.
 *
 * `ctx` shape used by the role/permission functions:
 *   { roles: string[], permissions: string[], isSuperAdmin: boolean, isMasterProvider: boolean }
 *
 * ---------------------------------------------------------------------------
 * Terminology — "partner" and "reseller" are NOT synonyms here
 * ---------------------------------------------------------------------------
 *
 * PARTNER — a tenant. The service provider (SP) row itself, the thing every
 *   request is scoped to by header. Two flags classify one:
 *     `is_master`      FoodAmigos' own provider. Exactly one exists. Screens
 *                      that are FoodAmigos tooling gate on this.
 *     `plg_onboarding` self-serve onboarding. Partners without it run a
 *                      FoodAmigos-like setup and keep surfaces a self-serve
 *                      partner never gets — so "partner" alone is never a
 *                      reason to hide something; say which flag you mean.
 *   In code: `serviceProvider`, `isMasterProvider`, `isPlgOnboardingProvider`,
 *   `useIsPartnerStaff` (a partner's own non-store manager).
 *
 * RESELLER — a manager role (`ManagerRole.Reseller`) held by a user *within* one
 *   provider, with its own screen groups and blocklist. Today it exists only on
 *   the FoodAmigos provider. It says nothing about which tenant you are in.
 *
 * So a "partner admin" is `ManagerRole.Admin` on a non-master SP (see
 * `PartnerAdminScreens`) and has nothing to do with `ManagerRole.Reseller`.
 * Prose in this repo predating this note sometimes says "reseller" where it
 * means partner staff; the identifiers are the authority.
 */

export const ManagerRole = {
  SuperAdmin: 'super_admin',
  Admin: 'admin',
  Reseller: 'reseller',
  SalesManager: 'sales_manager',
  SalesLeadership: 'sales_leadership',
  ContentManager: 'content_manager',
  OnboardingManager: 'onboarding_manager',
  MobileAppsManager: 'mobile_apps_manager',
  OccAgent: 'occ_agent',
  StoreAdmin: 'store_admin',
  StoreEmployee: 'store_employee',
  StoreOperator: 'store_operator',
  SDR: 'sdr',
};

export const ManagerPermission = {
  // Auth
  LoginOnBehalfOfMerchants: 'log_in_on_behalf_of_merchants',
  EditCompanyAccessControlList: 'edit_company_access_control_list',

  // Billing
  ManagePayouts: 'manage_payouts',
  EnableDailyPayouts: 'enable_daily_payouts',
  AccessPaymentGatewayDashboards: 'access_payment_gateway_dashboards',
  ManageSubscriptions: 'manage_subscriptions',
  CancelSubscriptions: 'subscriptions::cancel',
  EditSubscriptionPrices: 'edit_subscription_prices',
  EditSubscriptionPlanPrices: 'edit_subscription_plan_prices',
  EditAnnualContractDiscount: 'edit_annual_contract_discount',
  GrantElevatedAnnualContractDiscount: 'grant_elevated_annual_contract_discount',
  ManageSubscriptionTrial: 'subscriptions::manage_trial',
  CreateInternalInvoiceSubscriptions: 'create_internal_invoice_subscriptions',
  SetUnlimitedSubscriptionTrial: 'set_unlimited_subscription_trial',
  CreateInvoices: 'create_invoices',
  CreateInternalInvoices: 'create_internal_invoices',
  RechargeInvoices: 'recharge_invoices',
  WriteServiceFeeSettings: 'service_fee_settings::write',
  // A destination partner's Stripe processing-fee rates, its default and per restaurant.
  WriteProcessingFeeRates: 'processing_fee_rates::write',

  // Menu
  DeleteMenu: 'delete_menu',
  EditMenuProperties: 'edit_menu_properties',
  CreateMenuSmartPricingRules: 'create_menu_smart_pricing_rules',

  // Marketing & Promotions
  ReadMarketing: 'marketing::read',
  WriteMarketing: 'marketing::write',
  OrderPolygraphyProducts: 'polygraphy_products::order',
  CreateMasterPromoCodes: 'create_master_promo_codes',
  CreateAutomatedTrackingLinks: 'create_automated_tracking_links',
  ManageGiftCards: 'manage_gift_cards',

  // Team & Reporting
  ReadBillingHealthStats: 'read_billing_health_stats',

  // Profile & settings
  LaunchCompanies: 'launch_companies',
  SuspendCompanies: 'suspend_companies',
  DisableLiveOrders: 'disable_live_orders',
  ReadCompanySummary: 'company_summary::read',
  ChangeMerchantsEmail: 'change_merchants_email',
  MarkMerchantsEmailVerified: 'mark_merchants_email_verified',
  ManageExternalDelivery: 'manage_external_delivery',
  SwitchAutoAcceptOrders: 'switch_auto_accept_orders',
  WriteExternalDeliveryFee: 'external_delivery_fee::write',
  // Backs POST/PUT/PATCH /settings and /tax-categories on the shop backend.
  WriteCompanySettings: 'company_settings::write',

  // Google business profile
  DisconnectGoogleBusiness: 'disconnect_google_business',

  // Mobile apps
  WriteMobileApps: 'write_mobile_apps',
  ReleaseMobileApps: 'mobile_apps::release',

  // Other
  ExportCompanies: 'export_companies',
  DashboardViewRestaurantDetailsTab: 'dashboard_view_restaurant_details_tab',
  DashboardViewExternalDeliveryTab: 'dashboard_view_external_delivery_tab',

  // Stats
  ReadGlobalStats: 'global_stats::read',
  ReadCompanyStats: 'company_stats::read',
  ReadCustomerBaseStats: 'customer_base_stats::read',
  GlobalReadCustomerBaseStats: 'customer_base_stats::global_read',
  ReadSalesTeamStats: 'sales_team_stats::read',
  GlobalReadSalesTeamStats: 'sales_team_stats::global_read',

  // Reports
  ReadSignedLiveReports: 'read_signed_live_reports',
  FullAccessSignedLiveReports: 'full_access_signed_live_reports',
  FullAccessGeneralReports: 'full_access_general_reports',

  ManagePostLaunch: 'post_launch::manage',
  ManageOnboarding: 'onboarding::manage',
  WriteMasterMenu: 'master-menu::write',
  // Mirrors shop-backend ManagerPermission::WriteMenu. Note it does NOT carry the
  // inline "add a tax category" affordance in the menu modals: that posts to
  // /tax-categories, whose route group is gated on WriteCompanySettings alone.
  WriteMenu: 'menu::write',

  // Website
  ManageWebsiteStudio: 'website_studio::manage',
  DeleteStorefront: 'storefront::delete',
  ManageStorefrontSeo: 'storefront_seo::manage',

  // Business profiles — reading and writing the payment setup sits behind these too
  ManageBusinessProfiles: 'business_profiles::manage',
  WriteBusinessProfiles: 'business_profiles::write',

  // Business profile documents. Read = view + download the live documents; manage adds
  // the soft-deleted ones (`with_trashed`), which stay with the Admin wildcard.
  ReadBusinessProfilesDocs: 'business_profiles::read_docs',
  ManageBusinessProfilesDocs: 'business_profiles::manage_docs',
  WriteBusinessProfilesDocs: 'business_profiles::write_docs',
  // Upload documents once onboarding is done, on My Store → Documents or the finished Get Started
  // (PROD-543). Deleting stays with write_docs.
  UploadBusinessProfilesDocs: 'business_profiles::upload_docs',
  HardDeleteBusinessProfilesDocs: 'business_profiles::hard_delete_docs',

  // GDPR
  ApproveUserDeletionRequests: 'user_deletion_requests::approve',

  // AI suggestions (agent ops-queue)
  ManageAiSuggestions: 'ai_suggestions::manage',

  // Feature flags (company-level toggles)
  ReadFeatureFlags: 'feature_flags::read', // view-only: see the list
  ManageFeatureFlags: 'feature_flags::manage', // full write: all flags (Super/Admin wildcard)
  ManageExternalPrinterFlag: 'feature_flags::external_printer', // scoped write: external_printer only

  // Developers (Partner API self-service: api keys, webhooks, delivery history)
  ReadDeveloperSettings: 'developer_settings::read',
  ManageDeveloperSettings: 'developer_settings::manage',

  // The Partners page, split so partner setup and partner support can each be
  // delegated without handing over partner configuration. All four are withheld from
  // the Admin wildcard below, mirroring ManagerRole::SUPER_ADMIN_ONLY on the API.
  ReadPartners: 'partners::read', // see the partner list
  CreatePartners: 'partners::create', // create/invite + bulk import
  ManagePartners: 'partners::manage', // configure an existing partner, incl. suspend
  ImpersonatePartners: 'partners::impersonate', // open a partner's own dashboard as its admin
};

// Base Reseller permissions, shared with SalesManager/SalesLeadership. Feature-flag
// permissions are intentionally NOT in this base — they are added to the Reseller entry
// only, so they do not leak to SalesManager/SalesLeadership (FOO-4126).
const resellerBasePermissions = [
  ManagerPermission.ManagePayouts,
  ManagerPermission.ManageSubscriptions,
  ManagerPermission.EditSubscriptionPrices,
  ManagerPermission.EditAnnualContractDiscount,
  ManagerPermission.RechargeInvoices,

  ManagerPermission.CreateMasterPromoCodes,
  ManagerPermission.ManageGiftCards,

  // Marketing: shop-backend grants these via $resellerPermissions (inherited by
  // SalesManager/SalesLeadership); this FE base had drifted and omitted them, so
  // marketing UI gated on can(WriteMarketing) stayed hidden for these roles.
  // Re-aligned with the backend (FOO-5097).
  ManagerPermission.ReadMarketing,
  ManagerPermission.WriteMarketing,

  ManagerPermission.LaunchCompanies,
  ManagerPermission.ReadCompanySummary,
  ManagerPermission.MarkMerchantsEmailVerified,
  ManagerPermission.ManageExternalDelivery,

  // shop-backend grants this in $resellerPermissions (inherited by
  // SalesManager/SalesLeadership); this FE base had drifted and omitted it, so
  // My Store -> Tax & Currency stayed hidden for these roles and the inline
  // "add a tax category" control refused them. Re-aligned with
  // ManagerRole::permissions().
  ManagerPermission.WriteCompanySettings,

  ManagerPermission.ReadCompanyStats,
  ManagerPermission.ReadCustomerBaseStats,

  ManagerPermission.ManageStorefrontSeo,

  // My Store → Documents for any restaurant in reach, not only the ones this manager
  // is the designated sales manager of (PROD-524). Read-only: uploads, deletes and the
  // soft-deleted documents stay behind the *_docs permissions only Admin holds.
  // Mirrors $resellerPermissions in ManagerRole::permissions().
  ManagerPermission.ReadBusinessProfilesDocs,

  // Opens the Team → Reports "Signed / Live" tab, which used to be a hardcoded
  // [SalesManager, SalesLeadership, Reseller] role check here. Seeing the tab and
  // seeing the whole team in it are two different rights: the cross-manager scope is
  // FullAccessSignedLiveReports, which the Sales Manager tier holds and resellers must
  // not (FOO-8186). Mirrors ManagerRole::permissions() on the API.
  ManagerPermission.ReadSignedLiveReports,
];

// Permissions the Admin wildcard deliberately does NOT grant, so the Partners page
// can be gated by a permission that a partner Admin never holds. Mirrors
// ManagerRole::SUPER_ADMIN_ONLY on the API — the API is the security boundary.
const superAdminOnlyPermissions = [
  ManagerPermission.ReadPartners,
  ManagerPermission.CreatePartners,
  ManagerPermission.ManagePartners,
  ManagerPermission.ImpersonatePartners,
];

// The roles that may read a restaurant's business profile and its documents: the API's
// `business_profiles::manage`, which ManagerRole::permissions() gives these roles. The map below does
// not mirror that permission (only the Admin wildcard holds it here), so ask by role instead.
export const BusinessProfileReaderRoles = [
  ManagerRole.Admin,
  ManagerRole.Reseller,
  ManagerRole.SalesManager,
  ManagerRole.SalesLeadership,
  ManagerRole.OnboardingManager,
  ManagerRole.StoreAdmin,
  ManagerRole.StoreOperator,
];

export const RolePermissions = {
  [ManagerRole.SuperAdmin]: Object.values(ManagerPermission),
  [ManagerRole.Admin]: Object.values(ManagerPermission).filter(
    (permission) => !superAdminOnlyPermissions.includes(permission),
  ),

  [ManagerRole.Reseller]: resellerBasePermissions,
  get [ManagerRole.SalesManager]() {
    return [
      ...resellerBasePermissions,
      ManagerPermission.CreateInvoices,
      ManagerPermission.ReadGlobalStats,
      ManagerPermission.ReadSalesTeamStats,
      ManagerPermission.ManageOnboarding,
      // Cross-team scope for the Signed/Live report — the holder sees every seller's
      // numbers, so it stops at this tier and SalesLeadership inherits it below.
      ManagerPermission.FullAccessSignedLiveReports,
      // Sales managers create demo storefronts on non-production environments
      // and need to delete them there; deleting stays admin-only on production.
      ...(import.meta.env.VITE_APP_MODE !== ENVIRONMENTS.PROD ? [ManagerPermission.DeleteStorefront] : []),
    ];
  },
  get [ManagerRole.SalesLeadership]() {
    return [
      ...this[ManagerRole.SalesManager],
      ManagerPermission.GrantElevatedAnnualContractDiscount,
      ManagerPermission.FullAccessGeneralReports,
      ManagerPermission.GlobalReadCustomerBaseStats,
    ];
  },

  [ManagerRole.OnboardingManager]: [
    ManagerPermission.LoginOnBehalfOfMerchants,
    ManagerPermission.EditCompanyAccessControlList,

    ManagerPermission.CreateInvoices,
    ManagerPermission.RechargeInvoices,
    ManagerPermission.ManageSubscriptions,
    ManagerPermission.EditSubscriptionPrices,
    ManagerPermission.EditAnnualContractDiscount,

    ManagerPermission.DeleteMenu,
    ManagerPermission.EditMenuProperties,

    ManagerPermission.CreateMasterPromoCodes,
    ManagerPermission.CreateAutomatedTrackingLinks,
    ManagerPermission.ManageGiftCards,

    // Marketing: shop-backend grants onboarding managers both read + write
    // (ManagerRole::permissions()), but this FE map had drifted and omitted them,
    // so marketing-gated UI (e.g. the auto-flyer settings card, gated on
    // can(WriteMarketing)) stayed hidden for onboarding managers. Re-aligned to
    // the backend (FOO-5097).
    ManagerPermission.ReadMarketing,
    ManagerPermission.WriteMarketing,

    // shop-backend grants this in ManagerRole::permissions(); this FE map had
    // drifted and omitted it, so My Store -> Tax & Currency stayed hidden and
    // POST /tax-categories (route group: WriteCompanySettings) refused the
    // inline "add a tax category" control. Re-aligned with the backend.
    ManagerPermission.WriteCompanySettings,

    ManagerPermission.ReadBillingHealthStats,

    ManagerPermission.LaunchCompanies,
    ManagerPermission.SuspendCompanies,
    ManagerPermission.ReadCompanySummary,
    ManagerPermission.ChangeMerchantsEmail,
    ManagerPermission.MarkMerchantsEmailVerified,
    ManagerPermission.ManageExternalDelivery,

    ManagerPermission.DisconnectGoogleBusiness,

    ManagerPermission.ReadGlobalStats,
    ManagerPermission.ReadCompanyStats,
    ManagerPermission.ReadCustomerBaseStats,
    ManagerPermission.GlobalReadCustomerBaseStats,

    ManagerPermission.WriteMobileApps,
    ManagerPermission.ManageOnboarding,
    ManagerPermission.WriteMasterMenu,

    ManagerPermission.ManageWebsiteStudio,
    ManagerPermission.ManageStorefrontSeo,

    // My Store → Documents for any restaurant in reach (PROD-524), and uploads there and in the
    // finished Get Started, with the owner (PROD-543). Sales stay view/download only.
    ManagerPermission.ReadBusinessProfilesDocs,
    ManagerPermission.UploadBusinessProfilesDocs,

    // Feature flags: read the tab + toggle external_printer only (FOO-4126)
    ManagerPermission.ReadFeatureFlags,
    ManagerPermission.ManageExternalPrinterFlag,
    ManagerPermission.WriteMenu,
  ],
  [ManagerRole.MobileAppsManager]: [ManagerPermission.WriteMobileApps, ManagerPermission.ReleaseMobileApps],
  [ManagerRole.StoreAdmin]: [
    ManagerPermission.ReadGlobalStats,
    ManagerPermission.ManageGiftCards,
    ManagerPermission.WriteMasterMenu,

    // The owner's own KYC documents on My Store → Documents: view and download (PROD-524), and
    // upload, also after onboarding (PROD-543). Deleting stays with the admins. Mirrors the backend.
    ManagerPermission.ReadBusinessProfilesDocs,
    ManagerPermission.UploadBusinessProfilesDocs,

    // Marketing: shop-backend grants StoreAdmin both (ManagerRole::permissions());
    // this FE map had drifted and omitted them, hiding can(WriteMarketing) UI.
    // Re-aligned (FOO-5097).
    ManagerPermission.ReadMarketing,
    ManagerPermission.WriteMarketing,

    // shop-backend grants this in ManagerRole::permissions(); this FE map had
    // drifted and omitted it, so My Store -> Tax & Currency stayed hidden and
    // POST /tax-categories (route group: WriteCompanySettings) refused the
    // inline "add a tax category" control. Re-aligned with the backend.
    ManagerPermission.WriteCompanySettings,

    ManagerPermission.WriteMenu,
  ],
  [ManagerRole.StoreEmployee]: [ManagerPermission.ManageGiftCards],
  [ManagerRole.StoreOperator]: [
    ManagerPermission.ManageGiftCards,
    ManagerPermission.ReadGlobalStats,
    ManagerPermission.WriteMenu,
  ],

  // ContentManager and SDR had no RolePermissions entry at all, so can() was
  // false for every permission — including the menu rights shop-backend does
  // grant them (ManagerRole::permissions(): ReadMenu, WriteMenu, ImportMenu).
  // Only WriteMenu is mirrored here, because it is the only one this app
  // reads; the rest of the map stays as incomplete as it was rather than
  // being silently widened. Same class of drift as the StoreAdmin marketing
  // fix above.
  [ManagerRole.ContentManager]: [ManagerPermission.WriteMenu],
  [ManagerRole.SDR]: [ManagerPermission.WriteMenu],
};

// Which screen groups are accessible by the specific role
export const RoleScreenGroups = {
  [ManagerRole.SuperAdmin]: Object.values(ScreenGroupEnum),
  [ManagerRole.Admin]: Object.values(ScreenGroupEnum),
  [ManagerRole.Reseller]: [
    ScreenGroupEnum.OnlineShop,
    ScreenGroupEnum.Pricing,
    ScreenGroupEnum.ROI,
    ScreenGroupEnum.Marketing,
    ScreenGroupEnum.Reservations,
    ScreenGroupEnum.Website,
    ScreenGroupEnum.OrdersPortal,
    ScreenGroupEnum.Team,
    ScreenGroupEnum.DataDeletionRequests,
    // FoodAmigos-internal tools; MasterOnlyScreens keeps them off partner SPs.
    ScreenGroupEnum.ReportTechIssue,
    ScreenGroupEnum.PlatformAgent,
  ],
  get [ManagerRole.SalesManager]() {
    return [...this[ManagerRole.Reseller]];
  },
  get [ManagerRole.SalesLeadership]() {
    return this[ManagerRole.SalesManager];
  },
  [ManagerRole.ContentManager]: [
    ScreenGroupEnum.OnlineShop,
    ScreenGroupEnum.ReportTechIssue,
    ScreenGroupEnum.PlatformAgent,
  ],
  [ManagerRole.SDR]: [ScreenGroupEnum.OnlineShop, ScreenGroupEnum.ReportTechIssue, ScreenGroupEnum.PlatformAgent],
  [ManagerRole.OnboardingManager]: [
    ScreenGroupEnum.OnlineShop,
    ScreenGroupEnum.Pricing,
    ScreenGroupEnum.ROI,
    ScreenGroupEnum.Marketing,
    ScreenGroupEnum.Reservations,
    ScreenGroupEnum.Website,
    ScreenGroupEnum.OrdersPortal,
    ScreenGroupEnum.Team,
    ScreenGroupEnum.MobileApp,
    ScreenGroupEnum.PhoneAnswering,
    ScreenGroupEnum.DataDeletionRequests,
    ScreenGroupEnum.ReportTechIssue,
    ScreenGroupEnum.PlatformAgent,
  ],
  [ManagerRole.StoreAdmin]: [
    ScreenGroupEnum.OnlineShop,
    ScreenGroupEnum.Marketing,
    ScreenGroupEnum.Reservations,
    ScreenGroupEnum.Website,
    ScreenGroupEnum.OrdersPortal,
    ScreenGroupEnum.Team,
    ScreenGroupEnum.DataDeletionRequests,
  ],
  // Deliberately NOT granted Marketing, even though the WLP nav rework filed Gift
  // Cards under that section: counter staff have no business in a Marketing rail
  // section, and widening a role's screen groups to solve a menu-placement problem
  // is the wrong lever. useNavigation keeps their Gift Cards entry under Home
  // instead — see `showGiftCardsUnderHome` there.
  [ManagerRole.StoreEmployee]: [ScreenGroupEnum.OnlineShop, ScreenGroupEnum.Reservations],
  [ManagerRole.StoreOperator]: [ScreenGroupEnum.OnlineShop, ScreenGroupEnum.Reservations],
  [ManagerRole.MobileAppsManager]: [
    ScreenGroupEnum.MobileApp,
    ScreenGroupEnum.ReportTechIssue,
    ScreenGroupEnum.PlatformAgent,
  ],
  [ManagerRole.OccAgent]: [ScreenGroupEnum.ReportTechIssue, ScreenGroupEnum.PlatformAgent],
};

// Which screens exist only on the master service provider. Denied for everyone —
// SuperAdmins included — while a partner (non-master) SP is active: AI Suggestions
// is FA-internal tooling and Payment Gateways is master-only configuration.
// The tech-issue form files tickets with FoodAmigos' own triage team, so partner
// SPs never see it. The Amigo log is our team's view of every restaurant's requests.
export const MasterOnlyScreens = [
  ScreenEnum.AgentOpsQueue,
  ScreenEnum.PaymentGateways,
  ScreenEnum.ReportTechIssue,
  ScreenEnum.ReportTechIssueOverview,
  ScreenEnum.PlatformAgentLog,
];

// The partner self-service surface: only for Admin managers of a partner
// (non-master) service provider. Denied on the master provider and for every
// other role — SuperAdmins included, who manage partners via the Partners page.
export const PartnerAdminScreens = [
  ScreenEnum.PartnerAccount,
  ScreenEnum.PartnerBranding,
  ScreenEnum.PartnerPaymentGateway,
  ScreenEnum.PartnerBilling,
  ScreenEnum.PartnerTaxCurrency,
  ScreenEnum.PartnerServiceFeeCalculator,
  ScreenEnum.PartnerProcessingFees,
];

// Which screens are globally blocked except for admin role
export const GloballyBlockedScreens = [
  ScreenEnum.SubscriptionProducts,
  ScreenEnum.BillingIncomeReports,
  ScreenEnum.BillingTaxReports,
];

// Which screens are blocked for the specific role
// By default role has access to all screens within groups specified in RoleScreenGroups, unless listed here
export const RoleBlockedScreens = {
  [ManagerRole.Reseller]: [
    ScreenEnum.CustomerBase,
    ScreenEnum.SetupPackageFulfillments,
    ScreenEnum.MarketingPolygraphyProducts,
    ScreenEnum.MarketingPolygraphyOrders,
    ScreenEnum.BillingMonthlyStatements,
    ScreenEnum.PaymentGateways,
    ...GloballyBlockedScreens,
  ],
  [ManagerRole.SalesManager]: [
    ScreenEnum.SetupPackageFulfillments,
    ScreenEnum.MarketingPolygraphyOrders,
    ScreenEnum.BillingMonthlyStatements,
    ScreenEnum.PaymentGateways,
    ...GloballyBlockedScreens,
  ],
  // Sales leadership may order polygraphy products, so the orders screen stays accessible for them
  get [ManagerRole.SalesLeadership]() {
    return this[ManagerRole.SalesManager].filter((screen) => screen !== ScreenEnum.MarketingPolygraphyOrders);
  },
  [ManagerRole.ContentManager]: [
    ScreenEnum.GetStarted,
    // The partner-side equivalents of Get Started — same audience rules.
    ScreenEnum.NewRestaurant,
    ScreenEnum.RestaurantOnboarding,
    ScreenEnum.CustomerBase,
    ScreenEnum.Companies,
    ScreenEnum.Franchises,
    ScreenEnum.ExternalDeliverySupport,
    ScreenEnum.SetupPackageFulfillments,
    ScreenEnum.MarketingPolygraphyProducts,
    ScreenEnum.MarketingPolygraphyOrders,
    ScreenEnum.BillingMonthlyStatements,
    ScreenEnum.SalesOrders,
    ScreenEnum.SalesReceipts,
    ScreenEnum.SalesExternalDeliveries,
    ScreenEnum.BillingPayouts,
    ScreenEnum.BillingSubscription,
    ScreenEnum.BillingInvoices,
    ScreenEnum.MyStoreSettings,
    ScreenEnum.MyStoreBusinessInfo,
    ScreenEnum.MyStoreDocuments,
    ScreenEnum.MyStorePersonalInfo,
    ScreenEnum.MyStoreBankInfo,
    ScreenEnum.MyStoreTaxInfo,
    ScreenEnum.MyStoreShippingInfo,
    ScreenEnum.MyStoreGoogleProfile,
    ScreenEnum.MyStoreSeo,
    ScreenEnum.MyStoreTaxCurrency,
    ScreenEnum.OpeningHours,
    ScreenEnum.Locations,
    ScreenEnum.OrderSettingsTakeout,
    ScreenEnum.OrderSettingsMissedOrders,
    ScreenEnum.OrderSettingsPaymentMethods,
    ScreenEnum.OrderSettingsConnectPaypal,
    ScreenEnum.OrderSettingsTipSettings,
    ScreenEnum.OrderSettingsTerminal,
    ScreenEnum.OrderSettingsReceiptSettings,
    ScreenEnum.OrderSettingsStaffCredentials,
    ScreenEnum.OrderSettingsIntegrations,
    ScreenEnum.ContractAgreement,
    ScreenEnum.ContractTemplate,
    ScreenEnum.GiftCards,
    ScreenEnum.TeamManagers,
    ...GloballyBlockedScreens,
  ],
  get [ManagerRole.SDR]() {
    return this[ManagerRole.ContentManager];
  },
  [ManagerRole.StoreAdmin]: [
    ScreenEnum.GetStarted,
    // Creating a restaurant is partner-only. The onboarding wizard itself is
    // NOT blocked here: a StoreAdmin provisioned by a PLG partner continues
    // setup there (CanAccessOnboardingGuard admits exactly that case).
    ScreenEnum.NewRestaurant,
    ScreenEnum.CustomerBase,
    ScreenEnum.Companies,
    ScreenEnum.Franchises,
    ScreenEnum.ExternalDeliverySupport,
    ScreenEnum.PaymentGateways,
    ScreenEnum.SetupPackageFulfillments,
    ScreenEnum.MarketingPolygraphyProducts,
    ScreenEnum.MarketingPolygraphyOrders,
    ScreenEnum.BillingMonthlyStatements,
    ScreenEnum.SalesExternalDeliveries,
    ScreenEnum.MenuImport,
    ScreenEnum.MyStoreSettings,
    ScreenEnum.MyStoreGoogleProfile,
    ScreenEnum.OrderSettingsPaymentMethods,
    ScreenEnum.ContractTemplate,
    ScreenEnum.MarketingGoogleReviewProgram,
    ScreenEnum.TeamReports,
    ...GloballyBlockedScreens,
  ],
  [ManagerRole.StoreEmployee]: [
    ...GloballyBlockedScreens,
    ...Object.values(ScreenEnum).filter(
      (item) =>
        ![
          ScreenEnum.ReservationsSettings,
          ScreenEnum.ReservationsBookingAvailability,
          ScreenEnum.ReservationsOverview,
          ScreenEnum.ReservationsCreate,
          ScreenEnum.GiftCards,
        ].includes(item),
    ),
  ],
  [ManagerRole.StoreOperator]: [
    ...GloballyBlockedScreens,
    ...Object.values(ScreenEnum).filter(
      (item) =>
        ![
          ScreenEnum.ReservationsSettings,
          ScreenEnum.ReservationsBookingAvailability,
          ScreenEnum.ReservationsOverview,
          ScreenEnum.ReservationsCreate,
          ScreenEnum.GiftCards,
          ScreenEnum.Locations,
          ScreenEnum.Home,
          // `/online-shop/` redirects here, and the sidebar's Home item points
          // at it — allowing the group root but not this would leave a store
          // operator with no reachable landing page.
          ScreenEnum.Dashboard,
          ScreenEnum.SalesOrders,
          ScreenEnum.SalesReceipts,
          ScreenEnum.BillingPayouts,
          ScreenEnum.BillingInvoices,
          ScreenEnum.BillingSubscription,
          ScreenEnum.TopItems,
          ScreenEnum.OpeningHours,
          ScreenEnum.Menu,
          ScreenEnum.Modifiers,
          ScreenEnum.MenuEditHistory,
          ScreenEnum.SmartPricing,
          ScreenEnum.SoldOutItems,
          ScreenEnum.MenuImport,
          ScreenEnum.Offers,
          ScreenEnum.CreateOffers,
          ScreenEnum.UpdateOffers,
          ScreenEnum.OrderSettingsIntegrations,
          ScreenEnum.OrderSettingsConnectPaypal,
          // The operator runs the terminal, so they set its staff app login.
          ScreenEnum.OrderSettingsStaffCredentials,
        ].includes(item),
    ),
  ],
  [ManagerRole.OnboardingManager]: [...GloballyBlockedScreens],
};

/**
 * Whether the user holds any of the given roles. Super admins pass everything
 * unless `withSuperAdmin` is false.
 */
export function hasAnyRole({ roles = [], isSuperAdmin = false }, candidateRoles, withSuperAdmin = true) {
  if (withSuperAdmin && isSuperAdmin) {
    return true;
  }

  return collect(candidateRoles).intersect(roles).isNotEmpty();
}

/** Strict single-role check — true only when the user has exactly this one role. */
export function isRole({ roles = [] }, role) {
  return roles && roles.length === 1 && roles[0] === role;
}

/**
 * Roles ordered most- to least-significant, for the one-value-only surfaces fed
 * by `resolvePrimaryRole`. Where one role's permission set extends another's the
 * broader one sorts first (SalesLeadership ⊃ SalesManager ⊃ Reseller); the
 * lateral FoodAmigos-side roles follow, then the merchant-side store roles.
 */
const RolePrecedence = [
  ManagerRole.SuperAdmin,
  ManagerRole.Admin,
  ManagerRole.SalesLeadership,
  ManagerRole.SalesManager,
  ManagerRole.Reseller,
  ManagerRole.OnboardingManager,
  ManagerRole.ContentManager,
  ManagerRole.MobileAppsManager,
  ManagerRole.OccAgent,
  ManagerRole.SDR,
  ManagerRole.StoreAdmin,
  ManagerRole.StoreOperator,
  ManagerRole.StoreEmployee,
];

/**
 * The single most significant role the user holds, for surfaces that can carry
 * only one value — today the Intercom `user_role` attribute, since Intercom
 * custom attributes cannot hold a list. Super admins report `super_admin`,
 * whose flag lives outside `roles`. An unrecognised role is passed through
 * rather than dropped, so a role added backend-first still reports itself;
 * null only when the user holds no role at all.
 *
 * Note this says nothing about which tenant the user belongs to — see the
 * terminology block above. Partner staff hold ordinary roles, so a primary role
 * of `admin` covers both a FoodAmigos admin and a partner's own admin.
 */
export function resolvePrimaryRole({ roles = [], isSuperAdmin = false }) {
  if (isSuperAdmin) {
    return ManagerRole.SuperAdmin;
  }

  const held = roles ?? [];

  return RolePrecedence.find((role) => held.includes(role)) ?? held[0] ?? null;
}

/**
 * Whether the user is granted `permission` — via any of their roles' permission
 * sets or their explicit `permissions`. Accepts a string or an array of strings.
 * Super admins pass everything.
 */
export function can({ roles = [], permissions = [], isSuperAdmin = false }, permission) {
  if (isSuperAdmin) {
    return true;
  }

  return collect(RolePermissions)
    .filter((item, role) => roles.includes(role))
    .flatten()
    .merge(permissions)
    .intersect(collect(permission))
    .isNotEmpty();
}

/** Whether any of the user's roles grants access to the given screen group. */
export function canAccessScreenGroup({ roles = [] }, screenGroup) {
  return Boolean(collect(roles).first((item) => (RoleScreenGroups[item] ?? []).includes(screenGroup)));
}

/**
 * Whether the user can access a specific screen path. The screen's group is
 * derived from its first path segment; access requires the group to be allowed
 * AND the screen to not be in any of the user's role blocklists. Master-only
 * screens are additionally denied whenever the active service provider is not
 * the master — regardless of role, SuperAdmins included. Partner-admin screens
 * are the mirror image: they require a non-master provider AND the Admin role,
 * with no SuperAdmin bypass.
 */
export function canAccessScreen(ctx, screen) {
  // No screen ⇒ no specific screen to grant access to. Guard against a falsy
  // path (e.g. a removed/renamed ScreenEnum reference resolving to undefined) so
  // a stale link config degrades to "no access" instead of crashing the page.
  if (!screen) {
    return false;
  }

  if (MasterOnlyScreens.includes(screen) && !ctx.isMasterProvider) {
    return false;
  }

  if (
    PartnerAdminScreens.includes(screen) &&
    (ctx.isMasterProvider || !(ctx.roles ?? []).includes(ManagerRole.Admin))
  ) {
    return false;
  }

  const screenGroup = str(str(screen).ltrim('/').toString().split('/')[0]).padLeft('/').toString();

  return (
    canAccessScreenGroup(ctx, screenGroup) &&
    !collect(ctx.roles ?? []).first((item) => (RoleBlockedScreens[item] ?? []).includes(screen))
  );
}

/** Whether the company ACL grants global ('*') access. */
export function hasGlobalAccess(companyAcl) {
  const list = companyAcl ?? [];

  return Array.isArray(list) && list.includes('*');
}

/**
 * Resolve an access-control list to the set of ids the user may access.
 * A global list (`['*']`) is returned as-is; otherwise the list is scoped to the
 * active service provider's bucket.
 */
export function resolveAcl(aclList, serviceProviderId) {
  const list = aclList ?? [];

  if (Array.isArray(list) && list.includes('*')) {
    return list;
  }

  const providerList = list[serviceProviderId] ?? [];

  return collect(providerList).values().toArray();
}
